<template>
  <div class="task-card__header d-flex">
    <div
      class="task-card__avatar flex-shrink-0"
      :style="{
        backgroundColor: theme.avatarBg,
        color: theme.avatarColor,
      }"
    >
      {{ personInitials }}
    </div>
    <div class="task-card__body flex-grow-1">
      <div class="task-card__title-row d-flex align-items-start flex-wrap">
        <span class="task-card__name font-weight-bold">
          {{ item.response_from }}
        </span>
        <span
          class="task-card__badge"
          :style="{
            backgroundColor: theme.badgeBg,
            color: theme.badgeColor,
          }"
        >
          <i class="icon-phone-call task-card__badge-icon" aria-hidden="true"></i>
          {{ $t(theme.badgeLabelKey) }}
        </span>
        <span class="task-card__date text-muted ml-auto">
          {{ formattedDate }}
        </span>
      </div>
      <p class="task-card__subtitle text-muted mb-0">
        {{ $t("lang.opportunities.a_call") }}
        <span class="task-card__prospect-name">{{ item.name }}</span
        ><template v-if="item.comment"> · {{ item.comment }}</template>
      </p>
      <div
        v-if="item.event_id == '1' && item.has_task_ringover_registred == '1'"
        class="task-call-summary mt-3"
      >
        <button
          v-if="!summaryState.loading && !summaryState.html"
          type="button"
          class="task-call-summary-trigger w-100 d-flex align-items-center"
          @click="fetchCallSummary"
        >
          <i class="icon-magic task-call-summary-trigger__icon" aria-hidden="true"></i>
          <span class="flex-grow-1 text-left">{{
            $t("lang.opportunities.get_ai_summary")
          }}</span>
          <i class="icon-arrow-right task-call-summary-trigger__arrow" aria-hidden="true"></i>
        </button>
        <div
          v-if="summaryState.loading"
          class="task-call-summary-loading"
          role="status"
        >
          <span class="task-call-summary-loading__dots" aria-hidden="true">
            <span></span><span></span><span></span>
          </span>
          {{ $t("lang.opportunities.generating_ai_summary") }}
        </div>
        <div v-if="summaryState.error" class="task-call-summary-error small text-danger">
          {{ summaryState.error }}
        </div>
        <div v-if="summaryState.html" class="task-call-summary-panel">
          <button
            type="button"
            class="task-call-summary-panel__header w-100 d-flex align-items-center"
            @click="toggleCallSummaryExpanded"
          >
            <i class="icon-magic task-call-summary-panel__icon" aria-hidden="true"></i>
            <span class="flex-grow-1 text-left font-weight-bold">{{
              $t("lang.opportunities.ai_summary")
            }}</span>
            <i
              class="task-call-summary-panel__chevron"
              :class="summaryState.expanded ? 'icon-angle-up' : 'icon-angle-down'"
              aria-hidden="true"
            ></i>
          </button>
          <div
            v-show="summaryState.expanded"
            class="task-call-summary-content"
            v-html="summaryState.html"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { opportunitiesService } from "@/_services";

export default {
  name: "OpportunityOutputCard",
  props: {
    item: {
      type: Object,
      required: true,
    },
    theme: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      callSummaryById: {},
    };
  },
  computed: {
    personInitials() {
      return this.getPersonInitials(this.item.response_from);
    },
    formattedDate() {
      return this.formatTaskCardDate(this.item.creation_date);
    },
    summaryState() {
      return this.callSummaryState(this.item.id);
    },
  },
  methods: {
    getPersonInitials(name) {
      if (!name || typeof name !== "string") {
        return "?";
      }
      const trimmed = name.trim();
      if (!trimmed || trimmed.indexOf("@") !== -1) {
        return "?";
      }
      return trimmed
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    formatTaskCardDate(stringDate) {
      if (!stringDate) {
        return "";
      }
      const dateArray = stringDate.split(/-|\s|:/);
      const date = new Date(
        dateArray[0],
        dateArray[1] - 1,
        dateArray[2],
        dateArray[3] || 0,
        dateArray[4] || 0,
        dateArray[5] || 0
      );
      if (isNaN(date.getTime())) {
        return "";
      }
      const monthNames = [
        "janvier",
        "février",
        "mars",
        "avril",
        "mai",
        "juin",
        "juillet",
        "août",
        "septembre",
        "octobre",
        "novembre",
        "décembre",
      ];
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day} ${month}, ${hours}:${minutes}`;
    },
    callSummaryState(notificationId) {
      return (
        this.callSummaryById[notificationId] || {
          loading: false,
          html: "",
          error: null,
          expanded: false,
        }
      );
    },
    toggleCallSummaryExpanded() {
      const notificationId = this.item.id;
      const state = this.callSummaryState(notificationId);
      this.$set(this.callSummaryById, notificationId, {
        ...state,
        expanded: !state.expanded,
      });
    },
    fetchCallSummary() {
      const notificationId = this.item.id;
      this.$set(this.callSummaryById, notificationId, {
        loading: true,
        html: "",
        error: null,
        expanded: false,
      });
      opportunitiesService
        .ringoverTranscriptionByNotification(notificationId)
        .then((res) => {
          const html =
            res && res.data !== undefined && res.data !== null
              ? String(res.data)
              : "";
          this.$set(this.callSummaryById, notificationId, {
            loading: false,
            html,
            error: null,
            expanded: true,
          });
        })
        .catch((err) => {
          const message =
            typeof err === "string"
              ? err
              : (err && (err.message || err.error)) || "Request failed";
          this.$set(this.callSummaryById, notificationId, {
            loading: false,
            html: "",
            error: message,
            expanded: false,
          });
        });
    },
  },
};
</script>

<style scoped>
.task-card__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  margin-right: 12px;
}

.task-card__body {
  min-width: 0;
}

.task-card__title-row {
  gap: 8px;
  margin-bottom: 4px;
}

.task-card__name {
  font-size: 0.95rem;
  color: #111827;
}

.task-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.task-card__badge-icon {
  font-size: 0.7rem;
}

.task-card__date {
  font-size: 0.8rem;
  white-space: nowrap;
}

.task-card__subtitle {
  font-size: 0.85rem;
  line-height: 1.4;
}

.task-card__prospect-name {
  color: #374151;
}

.task-call-summary-trigger {
  background: #f3f0ff;
  color: #5850ec;
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  gap: 8px;
}

.task-call-summary-trigger:hover {
  background: #ebe6ff;
}

.task-call-summary-trigger__icon,
.task-call-summary-trigger__arrow {
  font-size: 0.9rem;
  flex-shrink: 0;
}

.task-call-summary-loading {
  background: #f3f4f6;
  border-radius: 8px;
  padding: 24px 16px;
  text-align: center;
  color: #5850ec;
  font-size: 0.875rem;
  font-weight: 500;
}

.task-call-summary-loading__dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 6px;
  vertical-align: middle;
}

.task-call-summary-loading__dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #5850ec;
  animation: task-summary-dot 1.2s infinite ease-in-out both;
}

.task-call-summary-loading__dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.task-call-summary-loading__dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes task-summary-dot {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

.task-call-summary-panel {
  background: #f8f7ff;
  border-radius: 8px;
  overflow: hidden;
}

.task-call-summary-panel__header {
  background: transparent;
  border: none;
  color: #5850ec;
  padding: 12px 14px;
  font-size: 0.875rem;
  cursor: pointer;
  gap: 8px;
}

.task-call-summary-panel__header:hover {
  background: #f3f0ff;
}

.task-call-summary-panel__icon {
  font-size: 0.9rem;
  flex-shrink: 0;
}

.task-call-summary-panel__chevron {
  font-size: 0.85rem;
  flex-shrink: 0;
}

.task-call-summary-content {
  font-size: 0.85rem;
  line-height: 1.5;
  overflow-x: auto;
  padding: 0 14px 14px;
  color: #374151;
}
</style>

<style>
.task-call-summary-content .call-summary h5 {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ca3af;
  margin: 14px 0 6px;
}

.task-call-summary-content .call-summary h5:first-child {
  margin-top: 0;
}

.task-call-summary-content .call-summary p,
.task-call-summary-content .call-summary li {
  margin-bottom: 0.5rem;
  color: #374151;
}

.task-call-summary-content .call-summary ul {
  padding-left: 1.25rem;
  margin-bottom: 0.5rem;
}

.task-call-summary-content .call-summary h5:last-of-type {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #5850ec;
  font-size: 0.65rem;
  margin-top: 16px;
  margin-bottom: 8px;
}

.task-call-summary-content .call-summary h5:last-of-type::before {
  font-family: "icomoon" !important;
  content: "\f024";
  font-size: 0.75rem;
  font-weight: normal;
}

.task-call-summary-content .call-summary h5:last-of-type + p {
  background: #ede9fe;
  border-radius: 8px;
  padding: 12px 14px;
  color: #1f2937;
  font-weight: 600;
  margin-bottom: 0;
}
</style>
